/**
 * Metal Conversion Slip Route — Unified Print Engine (AG-004).
 */
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMetalConversion } from "@/lib/metal-conversion-store";
import { PrintEngine } from "@/components/print-engine/PrintEngine";
import { useSettings } from "@/lib/settings-store";
import { shortShopName } from "@/lib/app-info";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/conversion/slip/$id")({
  head: () => {
    const shortName = shortShopName(useSettings.getState().firm?.shopName);
    return {
      meta: [{ title: `Conversion Slip · ${shortName} ERP` }],
    };
  },
  component: ConversionSlipPage,
});

function ConversionSlipPage() {
  const { id } = useParams({ from: "/conversion/slip/$id" });
  const records = useMetalConversion((s) => s.records);
  const record = records.find((r) => r.id === id);
  const [loading, setLoading] = useState(!record);

  useEffect(() => {
    let active = true;
    const fetchRecord = async () => {
      if (record) {
        setLoading(false);
        return;
      }
      try {
        await useMetalConversion.getState().refresh?.();
      } catch (e) {
        console.error("Failed to refresh metal conversion records:", e);
      } finally {
        if (active) setLoading(false);
      }
    };
    void fetchRecord();
    return () => {
      active = false;
    };
  }, [id, record]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-background text-foreground">
        <div className="text-center space-y-3 flex flex-col items-center">
          <Loader2 className="h-8 w-8 text-gold animate-spin" />
          <p className="text-sm text-muted-foreground">Loading Conversion Slip…</p>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground p-6">
        <div className="text-center space-y-3">
          <h1 className="font-serif text-2xl text-gold">Conversion slip not available</h1>
          <Link to="/conversion" className="text-gold underline">
            Back to Metal Conversion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PrintEngine
      docType="metal_conversion_slip"
      recordId={record.id}
      backUrl="/conversion"
    />
  );
}
